import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox, Input, Switch } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { AccessToken } from "../../../config/AccessToken";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { Card, CardBody, CardHeader, CardTitle } from "@progress/kendo-react-layout";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loadingPanel } from "../../LoadingPanel";


export const Report = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const localizationService = useLocalization();
    const [instance, accessTokenRequest] = AccessToken();
    let languageId: any = localStorage.getItem('languageId');
    const isEnglishLanguage = languageId === 'en-US';

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [confirmMessage, setConfirmMessage] = useState<{ reportId: number, message: string } | null>(null);

    const [isAdvanced, setIsAdvanced] = useState(false);
    const [typeReportAdvanced, setTypeReportAdvanced] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>(null);
    const [dateTimeRange, setDateTimeRange] = useState<{
        fromDate: Date | null;
        fromTime: Date | null;
        toDate: Date | null;
        toTime: Date | null;
    }>({
        fromDate: new Date(),
        toDate: new Date(),
        fromTime: new Date(),
        toTime: new Date()
    });
    const [checked, setChecked] = useState<Record<number, boolean>>({});

    const [selectedDays, setSelectedDays] = useState<number[]>([]);
    const [hour, setHour] = useState<number>(12);

    const [scheduledReports, setScheduledReports] = useState<Array<{
        id?: number;
        typeDescription: string;
        typeId: number;
        reportTypes: number[];
        days: string;
        hour: string;
    }>>([]);

    const [selectedTypes, setSelectedTypes] = useState<number[]>([]);

    const isExportDisabled = selectedTypes.length === 0 ||
        (!isAdvanced && !selectedOption) ||
        (isAdvanced && (!dateTimeRange.fromDate || !dateTimeRange.toDate));

    const isExecuteDisabled = !selectedOption || selectedTypes.length === 0 || selectedDays.length === 0 || hour === null || hour === undefined;


    const options = [
        {
            "id": -1,
            "name": localizationService.toLanguageString('configuration.analysis.path_history.custom', 'NOT_FOUND'),
            "value": -1
        },
        {
            "id": 0,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_1_hour', 'NOT_FOUND'),
            "value": 60
        },
        {
            "id": 1,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_3_hours', 'NOT_FOUND'),
            "value": 180
        },
        {
            "id": 2,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_24_hours', 'NOT_FOUND'),
            "value": 1440
        },
        {
            "id": 3,
            "name": localizationService.toLanguageString('configuration.analysis.report.today', 'NOT_FOUND'),
            "value": 0
        },
        {
            "id": 4,
            "name": localizationService.toLanguageString('configuration.analysis.report.from_yesterday', 'NOT_FOUND'),
            "value": 1
        },
        {
            "id": 5,
            "name": localizationService.toLanguageString('configuration.analysis.report.last_3_days', 'NOT_FOUND'),
            "value": 4320
        },
        {
            "id": 6,
            "name": localizationService.toLanguageString('configuration.analysis.report.this_week', 'NOT_FOUND'),
            "value": 10080
        },
        {
            "id": 7,
            "name": localizationService.toLanguageString('configuration.analysis.report.this_month', 'NOT_FOUND'),
            "value": 43200
        }
    ];

    const checkboxLabels = [
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.signals', 'NOT_FOUND'),
            "id": 1
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.zones_access', 'NOT_FOUND'),
            "id": 2
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.devices_battery', 'NOT_FOUND'),
            "id": 3
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.distance_traveled', 'NOT_FOUND'),
            "id": 4
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.working_time', 'NOT_FOUND'),
            "id": 5
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.devices_status', 'NOT_FOUND'),
            "id": 6
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.distance_traveled_odometer', 'NOT_FOUND'),
            "id": 7
        }
    ];

    const lastReportTypeId = checkboxLabels[checkboxLabels.length - 1]?.id;
    const showAuxFields = !typeReportAdvanced && lastReportTypeId !== undefined && selectedTypes.includes(lastReportTypeId);

    const days = [
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.monday', 'NOT_FOUND'),
            "value": 1
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.tuesday', 'NOT_FOUND'),
            "value": 2
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.wednesday', 'NOT_FOUND'),
            "value": 3
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.thursday', 'NOT_FOUND'),
            "value": 4
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.friday', 'NOT_FOUND'),
            "value": 5
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.saturday', 'NOT_FOUND'),
            "value": 6
        },
        {
            "name": localizationService.toLanguageString('configuration.analysis.report.sunday', 'NOT_FOUND'),
            "value": 0
        }
    ];

    const formatScheduledDays = (rawDays: string): string => {
        if (!rawDays || typeof rawDays !== 'string') {
            return '';
        }

        const labelsByValue = new Map(days.map((day) => [day.value, day.name]));
        const tokens = rawDays
            .split(',')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);

        const numericDays = tokens
            .map((token) => Number(token))
            .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);

        if (numericDays.length > 0) {
            const orderedNumericDays = Array.from(new Set(numericDays)).sort((a, b) => a - b);
            return orderedNumericDays
                .map((dayValue) => labelsByValue.get(dayValue) || String(dayValue))
                .join(', ');
        }

        const orderByName = new Map(days.map((day) => [day.name.toLowerCase(), day.value]));

        return tokens
            .sort((a, b) => {
                const aKey = a.toLowerCase();
                const bKey = b.toLowerCase();
                const aOrder = orderByName.get(aKey) ?? Number.MAX_SAFE_INTEGER;
                const bOrder = orderByName.get(bKey) ?? Number.MAX_SAFE_INTEGER;
                return aOrder - bOrder;
            })
            .join(', ');
    };

    const toggleType = (value: number) => {
        setSelectedTypes((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value]
        );
    };

    const formatDateTimeUtc = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const seconds = date.getUTCSeconds().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const toCustomWeekDayIndex = (jsDay: number): number => {
        // JS: Sunday=0..Saturday=6, App: Monday=0..Sunday=6
        return (jsDay + 6) % 7;
    };

    const convertScheduledTimeToUtc = (days: number[], localHour: number) => {
        const referenceLocal = new Date();
        referenceLocal.setHours(localHour, 0, 0, 0);

        const utcHour = referenceLocal.getUTCHours().toString().padStart(2, "0");
        const utcMinutes = referenceLocal.getUTCMinutes().toString().padStart(2, "0");
        const utcSeconds = referenceLocal.getUTCSeconds().toString().padStart(2, "0");

        const referenceCustomDay = toCustomWeekDayIndex(referenceLocal.getDay());
        const utcDays = days.map((day) => {
            const dayDate = new Date(referenceLocal);
            dayDate.setDate(referenceLocal.getDate() + (day - referenceCustomDay));
            return toCustomWeekDayIndex(dayDate.getUTCDay());
        });

        return {
            days: Array.from(new Set(utcDays)),
            hour: `${utcHour}:${utcMinutes}:${utcSeconds}`
        };
    };

    const handleExport = async (format: "csv" | "xlsx") => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            let fromDateTime: string;
            let toDateTime: string;

            if (isAdvanced) {
                // Modalità custom: usa fromDate/fromTime e toDate/toTime
                const fromDateObj = dateTimeRange.fromDate || new Date();
                const fromTimeObj = dateTimeRange.fromTime || new Date();
                const toDateObj = dateTimeRange.toDate || new Date();
                const toTimeObj = dateTimeRange.toTime || new Date();

                // Combina data e ora locale, poi converte in stringa UTC per il backend
                const combinedFrom = new Date(
                    fromDateObj.getFullYear(),
                    fromDateObj.getMonth(),
                    fromDateObj.getDate(),
                    fromTimeObj.getHours(),
                    fromTimeObj.getMinutes(),
                    fromTimeObj.getSeconds(),
                    0
                );

                const combinedTo = new Date(
                    toDateObj.getFullYear(),
                    toDateObj.getMonth(),
                    toDateObj.getDate(),
                    toTimeObj.getHours(),
                    toTimeObj.getMinutes(),
                    toTimeObj.getSeconds(),
                    0
                );

                fromDateTime = formatDateTimeUtc(combinedFrom);
                toDateTime = formatDateTimeUtc(combinedTo);
            } else {
                // Modalità predefinita: calcola basandosi su selectedOption.value (minuti)
                // Lavora in ora locale per avere la mezzanotte corretta
                const now = new Date();
                now.setSeconds(0);
                now.setMilliseconds(0);

                // Caso speciale per "oggi" (id 3): parte dalla mezzanotte locale
                if (selectedOption.id === 3) {
                    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                    fromDateTime = formatDateTimeUtc(midnight);
                    toDateTime = formatDateTimeUtc(now);
                }
                // Caso speciale per "da ieri" (id 4): parte dalla mezzanotte del giorno precedente locale
                else if (selectedOption.id === 4) {
                    const yesterday = new Date(now);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayMidnight = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
                    fromDateTime = formatDateTimeUtc(yesterdayMidnight);
                    toDateTime = formatDateTimeUtc(now);
                }
                else {
                    // Per gli altri casi (ultime X ore): sottrai i minuti dall'ora corrente
                    const minutesAgo = selectedOption.value;
                    const fromDate = new Date(now.getTime() - minutesAgo * 60000);
                    fromDateTime = formatDateTimeUtc(fromDate);
                    toDateTime = formatDateTimeUtc(now);
                }
            }

            const payload = {
                reportIds: selectedTypes,
                fromDatetime: fromDateTime,
                toDatetime: toDateTime,
                exportType: format === "csv" ? "CSV" : "Excel"
            };

            try {
                await axios.post(`${SERVER_URL}/report`, payload, { headers });

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.email_sent_success', 'Report inviato via email con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.export_error', 'Errore durante l\'esportazione del report') + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };


    // Gestione checkbox
    const toggleDay = (value: number) => {
        setSelectedDays((prev) =>
            prev.includes(value)
                ? prev.filter((d) => d !== value)
                : [...prev, value]
        );
    };

    const mergeDateAndTime = (date: Date, time: Date) => {
        const merged = new Date(date);
        merged.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
        return merged;
    };

    const isValidYear = (date: Date) => {
        const year = date.getFullYear();
        return year >= 1000;
    };

    const handleChangeDate = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.value ?? event?.target?.value;

        if (!name) {
            return;
        }

        if (value && !isValidYear(value)) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === "toDate" && value && prev.fromDate && value < prev.fromDate) {
                return prev;
            }

            if (name === "fromDate" && value && prev.fromTime) {
                updated.fromTime = mergeDateAndTime(value, prev.fromTime);
            }

            if (name === "toDate" && value && prev.toTime) {
                updated.toTime = mergeDateAndTime(value, prev.toTime);
            }

            return updated;
        });
    };

    const handleBlurDate = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.target?.value;

        if (!name || !value) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev };

            if (name === "toDate" && prev.fromDate && value < prev.fromDate) {
                updated.toDate = null;
            }

            if (name === "fromDate" && prev.toDate && value > prev.toDate) {
                updated.toDate = null;
            }

            return updated;
        });
    };

    const handleChangeTime = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.value ?? event?.target?.value;

        if (!name) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev, [name]: value };

            if (name === "toTime" && value && prev.fromTime && value < prev.fromTime) {
                return prev;
            }

            if (name === "fromTime" && value && prev.fromDate) {
                updated.fromDate = mergeDateAndTime(prev.fromDate, value);
            }

            if (name === "toTime" && value && prev.toDate) {
                updated.toDate = mergeDateAndTime(prev.toDate, value);
            }

            return updated;
        });
    };

    const handleBlurTime = (event: any) => {
        const name = event?.target?.props?.name ?? event?.target?.name;
        const value = event?.target?.value;

        if (!name || !value) {
            return;
        }

        setDateTimeRange((prev) => {
            const updated = { ...prev };

            if (name === "toTime" && prev.fromTime && value < prev.fromTime) {
                updated.toTime = null;
            }

            if (name === "fromTime" && prev.toTime && value > prev.toTime) {
                updated.fromTime = null;
            }

            return updated;
        });
    };

    // Formatta l'ora come HH:MM
    const formatHour = (v: number) => `${v.toString().padStart(2, "0")}:00`;

    const formatScheduledUtcHourToLocal = (utcHour: string): string => {
        if (!utcHour) {
            return "";
        }

        const parts = utcHour.split(":").map((value) => Number(value));
        if (parts.length < 2 || parts.some((value) => Number.isNaN(value))) {
            return utcHour;
        }

        const [hours, minutes, seconds = 0] = parts;
        const referenceDate = new Date();
        referenceDate.setUTCHours(hours, minutes, seconds, 0);

        const localHours = referenceDate.getHours().toString().padStart(2, "0");
        const localMinutes = referenceDate.getMinutes().toString().padStart(2, "0");

        return `${localHours}:${localMinutes}`;
    };

    const toPickerTime = (hoursValue: number): Date => {
        const value = new Date();
        value.setHours(hoursValue, 0, 0, 0);
        return value;
    };

    const handleHourChange = (event: any) => {
        const value = event?.value;
        if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
            return;
        }

        setHour(value.getHours());
    };

    const execute = async () => {
        if (!selectedOption || selectedDays.length === 0 || hour === null || hour === undefined) {
            setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.complete_parameters', 'Compila tutti i parametri prima di eseguire.'));
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            const { days: utcDays, hour: utcHour } = convertScheduledTimeToUtc(selectedDays, Math.round(hour));

            const payload = {
                reportIds: selectedTypes,
                typeId: selectedOption.id,
                hour: utcHour,
                days: utcDays
            };

            try {
                await axios.post(`${SERVER_URL}/report/scheduled`, payload, { headers });

                // Ricarica la lista dei report schedulati dopo il successo
                await getReportList();

                // Reset dei campi
                setSelectedTypes([]);
                setSelectedDays([]);
                setHour(12);
                setSelectedOption(null);

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.scheduled_success', 'Report schedulato con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.scheduling_error', 'Errore durante la schedulazione del report') + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };

    const getReportList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            await axios.get(`${SERVER_URL}/report/scheduled`, { headers })
                .then((res) => {
                    const data: any = res?.data?.data
                    // Mappa i dati del server per adattarli alla struttura frontend
                    const mappedData = data.map((item: any) => ({
                        ...item,
                        reportTypes: Array.isArray(item.reportId) ? item.reportId : [item.reportId]
                    }));
                    setScheduledReports(mappedData);
                    setLoading(false);
                }).catch((data) => {
                    //setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const deleteScheduledReport = async (reportId: number) => {
        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.delete(`${SERVER_URL}/report/scheduled/${reportId}`, { headers });

                // Ricarica la lista dei report schedulati dopo l'eliminazione
                await getReportList();

                setSuccessMessage(localizationService.toLanguageString('configuration.analysis.report.deleted_success', 'Report eliminato con successo!'));
            } catch (error: any) {
                console.error(error);
                setErrorMessage(localizationService.toLanguageString('configuration.analysis.report.deletion_error', "Errore durante l'eliminazione del report") + ": " + (error?.response?.data?.message || error?.message));
            } finally {
                setLoading(false);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
    };

    //  call API on page load
    useEffect(() => {
        if (typeReportAdvanced) {
            getReportList()
        }
    }, [typeReportAdvanced])

    // Auto-close success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Auto-close error message after 3 seconds
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => {
                setErrorMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    // Auto-close confirm message after 3 seconds
    useEffect(() => {
        if (confirmMessage) {
            const timer = setTimeout(() => {
                setConfirmMessage(null);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [confirmMessage]);

    const onChangeIntervalTime = (ev: any) => {
        const value = ev.value;
        setSelectedOption(value);
        if (value && value.id === -1) {
            setIsAdvanced(true);
        } else {
            setIsAdvanced(false);
        }
    };

    const onChangeReportType = (ev: any) => {
        const nextIsScheduled = Boolean(ev.value);
        setTypeReportAdvanced(nextIsScheduled);

        if (nextIsScheduled) {
            // Scheduled mode does not use custom date-range option (-1).
            if (selectedOption?.id === -1) {
                setSelectedOption(options.find((option) => option.id === 0) ?? null);
            }
            setIsAdvanced(false);
            return;
        }

        // If user goes back to standard with custom option selected, restore custom controls.
        if (selectedOption?.id === -1) {
            setIsAdvanced(true);
        }
    };

    const reportLayoutStyle: React.CSSProperties = {
        margin: "20px",
        gap: "20px",
        width: "calc(100% - 40px)",
        alignItems: "start"
    };

    const panelStyle: React.CSSProperties = {
        borderRadius: '14px',
        border: '1px solid #c3dce2',
        background: 'linear-gradient(180deg, #ffffff 0%, #f6fbfc 100%)',
        boxShadow: '0 12px 24px rgba(8, 69, 78, 0.1)',
        padding: "18px",
        marginTop: "20px"        
    };

    const reportCss = `
        @keyframes reportFadeLift {
            from {
                opacity: 0;
                transform: translateY(8px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .report-panel {
            animation: reportFadeLift 280ms ease-out;
        }

        .report-layout .k-button {
            transition: transform 140ms ease, box-shadow 180ms ease, filter 180ms ease;
        }

        .report-layout .k-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(16, 58, 97, 0.16);
        }

        .report-layout .k-button:focus-visible {
            outline: 2px solid #2f7eb8;
            outline-offset: 2px;
        }

        .report-layout h1 {
            margin: 0 0 10px;
            font-size: var(--fs-page-title);
            line-height: 1.2;
            color: #0f3556;
            letter-spacing: 0.01em;
        }

        .report-toggle {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 8px;
            padding: 10px 12px;
            border-radius: 12px;
            background: #eef4fb;
            border: 1px solid #d3e2f1;
            min-width: 360px;
        }

        .report-toggle-types-shell {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 20px;
            margin-top: 8px;
            margin-bottom: 12px;
            flex-wrap: wrap;
        }

        .report-toggle-column {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex: 1 1 360px;
            max-width: 500px;
            min-width: 280px;
        }

        .report-types-box {
            margin-left: auto;
            flex: 1 1 360px;
            width: 100%;
            max-width: 500px;
        }

        .report-option-row {
            max-width: 500px;
            margin: 12px 0 20px;
        }

        .report-section-title {
            margin: 0 0 10px;
            font-size: 1rem;
            color: #16486c;
            letter-spacing: 0.01em;
        }

        .report-advanced-block {
            max-width: 500px;
            margin: 12px 0 18px;
            padding: 12px;
            border: 1px dashed #b9d0e6;
            border-radius: 12px;
            background: rgba(247, 251, 255, 0.9);
        }

        .report-date-time-row {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 10px;
            flex-wrap: wrap;
        }

        .report-date-time-label {
            width: 34px;
            color: #1c4668;
            font-weight: 600;
        }

        .report-time-control {
            margin-left: 2px;
        }

        .report-date-time-row .k-datepicker,
        .report-date-time-row .k-timepicker {
            width: 190px;
            max-width: 100%;
        }

        .report-types-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(210px, 1fr));
            gap: 8px 14px;
            margin-top: 0;
        }

        .report-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            border-top: 1px solid #d7e4f2;
            margin-top: 16px;
            padding-top: 14px;
            flex-wrap: wrap;
        }

        .report-actions .k-button {
            min-width: 160px;
            border-radius: 10px;
        }

        .report-days-box,
        .report-slider-box {
            background: linear-gradient(180deg, #f7fbff 0%, #edf5fd 100%);
            border: 1px solid #d2e1f0;
            border-radius: 12px;
            padding: 12px;
            margin-bottom: 20px;
            max-width: 710px;
        }

        .report-scheduled-controls {
            display: flex;
            align-items: flex-start;
            gap: 20px;
            flex-wrap: wrap;
            border-top: 1px solid #d7e4f2;
        }

        .report-scheduled-controls .report-days-shell {
            flex: 1 1 520px;
            max-width: none;
            margin-bottom: 0;
        }

        .report-scheduled-controls .report-slider-box {
            flex: 0 0 180px;
            min-width: 180px;
            max-width: 280px;
            margin-bottom: 0;
        }

        .report-scheduled-card {
            transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
        }

        //.report-scheduled-card:hover {
        //    transform: translateY(-2px);
        //    border-color: #9bc8d1;
        //    box-shadow: 0 16px 28px rgba(8, 69, 78, 0.16);
        //}

        .report-scheduled-card .k-card-header {
            background: linear-gradient(180deg, #eaf6f9 0%, #e0f1f5 100%);
            border-bottom: 1px solid #d0e6ec;
        }

        .report-scheduled-card .k-card-body {
            padding-top: 1rem;
        }

        .report-days-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 10px 14px;
        }

        .report-day-item {
            min-width: 84px;
            text-align: center;
            font-size: 12px;
            color: #214666;
            padding: 8px 6px;
            border-radius: 10px;
            border: 1px solid #cfdeec;
            background: #f6fbff;
        }

        .report-hour-badge {
            display: inline-block;
            margin-top: 8px;
            margin-left: 20px;
            padding: 4px 10px;
            color: #0d4e7c;
            border-radius: 999px;
            border: 1px solid #a8c7e2;
            background: #e8f4ff;
            font-weight: 600;
            font-size: 13px;
        }

        .report-scheduled-table-wrapper {
            width: 100%;
            overflow-x: auto;
            max-height: 380px;
            border-radius: 10px;
            border: 1px solid #d5e3f0;
            background: #E2F2F6;
        }

        .report-scheduled-table {
            width: 100%;
            border-collapse: collapse;
            min-width: 680px;
        }

        .report-scheduled-table thead tr {
            background: #E2F2F6;
        }

        .report-scheduled-table th,
        .report-scheduled-table td {
            border: 1px solid #c8d9eb;
            padding: 8px;
            vertical-align: top;
        }

        .report-scheduled-table thead th {
            position: sticky;
            top: 0;
            z-index: 1;
        }

        .report-scheduled-table tbody tr:nth-child(even) {
            background: #f8fbff;
        }

        .report-scheduled-table th {
            font-weight: 700;
            color: #133d60;
        }

        .report-scheduled-table td {
            color: #1c425f;
        }

        .report-delete-btn {
            white-space: nowrap;
        }

        .report-aux-fields {
            margin-left: 8px;
            margin-top: 8px;
            padding-left: 10px;
            border-left: 2px solid #c8daec;
            display: grid;
            gap: 10px;
        }

        @media (max-width: 1200px) {
            .report-layout {
                grid-template-columns: 1fr !important;
            }
        }

        @media (max-width: 760px) {
            .report-layout {
                margin: 12px !important;
                width: calc(100% - 24px) !important;
            }

            .report-types-grid {
                grid-template-columns: 1fr;
            }

            .report-toggle-types-shell {
                flex-direction: column;
                gap: 12px;
            }

            .report-toggle-column {
                max-width: 100%;
                min-width: 0;
            }

            .report-types-box {
                margin-left: 0;
                max-width: 100%;
            }

            .report-actions {
                justify-content: stretch;
            }

            .report-actions .k-button {
                width: 100%;
                min-width: 0;
            }

            .report-toggle {
                width: 100%;
                justify-content: space-between;
                min-width: 0;
            }

            .report-days-shell {
                flex-direction: column;
                gap: 10px !important;
            }

            .report-scheduled-controls {
                flex-direction: column;
            }

            .report-scheduled-controls .report-days-shell,
            .report-scheduled-controls .report-slider-box {
                flex: 1 1 auto;
                max-width: 100%;
                min-width: 0;
                width: 100%;
            }

            .report-days-title {
                width: auto !important;
                margin: 0;
            }

            .report-date-time-row .k-datepicker,
            .report-date-time-row .k-timepicker {
                width: 100% !important;
            }

            .report-time-control {
                margin-left: 0;
                width: 100%;
            }
        }

        @media (max-width: 420px) {
            .report-panel {
                border-radius: 12px;
            }

            .report-layout h1 {
                font-size: var(--fs-page-title);
            }

            .report-toggle {
                padding: 8px 10px;
                gap: 8px;
            }

            .report-day-item {
                min-width: 72px;
                padding: 6px 4px;
            }

            .report-date-time-row {
                gap: 6px;
            }

            .report-date-time-label {
                width: 100%;
                margin-bottom: 2px;
            }

            .report-actions {
                gap: 8px;
                margin-top: 12px;
                padding-top: 12px;
            }

            .report-scheduled-table-wrapper {
                max-height: 320px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .report-panel,
            .report-layout .k-button {
                animation: none !important;
                transition: none !important;
            }
        }
    `;

    const settingTitleStyle = {
        color: '#0b4a53',
        fontWeight: 800,
        letterSpacing: '0.015em'
    };

    const scheduledCardStyle: React.CSSProperties = {
        minWidth: '0',
        width: 'auto',
        flex: '1 1 0',
        borderRadius: '14px',
        border: '1px solid #c3dce2',
        background: 'linear-gradient(180deg, #ffffff 0%, #f6fbfc 100%)',
        boxShadow: '0 12px 24px rgba(8, 69, 78, 0.1)',
        overflow: 'hidden',
        marginTop: '20px'
    };

    return (
        <>
            <style>{reportCss}</style>

            <div className="report-layout" style={reportLayoutStyle}>
                {loading && loadingPanel}

                <NotificationGroup
                    style={{
                        right: 0,
                        bottom: 50,
                        zIndex: 1200,
                        alignItems: "flex-start",
                        flexWrap: "wrap-reverse",
                    }}
                >
                    <Fade enter={true} exit={true}>
                        {successMessage && (
                            <Notification
                                type={{ style: "success", icon: true }}
                                closable={true}
                                onClose={() => setSuccessMessage(null)}
                            >
                                <span>{successMessage}</span>
                            </Notification>
                        )}
                        {errorMessage && (
                            <Notification
                                type={{ style: "error", icon: true }}
                                closable={true}
                                onClose={() => setErrorMessage(null)}
                            >
                                <span>{errorMessage}</span>
                            </Notification>
                        )}
                        {confirmMessage && (
                            <Notification
                                type={{ style: "warning", icon: true }}
                                closable={true}
                                onClose={() => setConfirmMessage(null)}
                            >
                                <div>
                                    <div style={{ marginBottom: '10px' }}>{confirmMessage.message}</div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <Button
                                            themeColor="error"
                                            size="small"
                                            onClick={() => {
                                                deleteScheduledReport(confirmMessage.reportId);
                                                setConfirmMessage(null);
                                            }}
                                        >
                                            {localizationService.toLanguageString('configuration.analysis.report.confirm', 'NOT_FOUND')}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => setConfirmMessage(null)}
                                        >
                                            {localizationService.toLanguageString('configuration.analysis.report.cancel', 'NOT_FOUND')}
                                        </Button>
                                    </div>
                                </div>
                            </Notification>
                        )}
                    </Fade>
                </NotificationGroup>

                <div className="report-panel" style={panelStyle}>
                    <h1>{localizationService.toLanguageString('configuration.analysis.report.report_title', 'NOT_FOUND')}</h1>

                    <div className="report-toggle-types-shell">
                        <div className="report-toggle-column">
                            <Tooltip position="top" anchorElement="target">
                                <div className="report-toggle">
                                    <span
                                        style={{ marginRight: '10px' }}
                                        title={localizationService.toLanguageString('configuration.analysis.report.switch_report_type_tooltip', 'NOT_FOUND')}
                                    >
                                        {localizationService.toLanguageString('configuration.analysis.report.switch_report_type', 'NOT_FOUND')}
                                    </span>
                                    <Switch
                                        onChange={onChangeReportType}
                                        checked={typeReportAdvanced}
                                        size={'large'}
                                    />
                                    <b style={{ marginLeft: '10px' }}>
                                        {!typeReportAdvanced ?
                                            localizationService.toLanguageString('configuration.analysis.report.standard', 'NOT_FOUND')
                                            :
                                            localizationService.toLanguageString('configuration.analysis.report.scheduled', 'NOT_FOUND')
                                        }
                                    </b>
                                </div>
                            </Tooltip>

                            {/* Dropdown or DatePickers */}
                            <div className="report-option-row">
                                <DropDownList
                                    data={options}
                                    textField="name"
                                    dataItemKey="value"
                                    style={{ width: '100%', maxWidth: '710px' }}
                                    value={selectedOption}
                                    onChange={onChangeIntervalTime}
                                    label={localizationService.toLanguageString('configuration.analysis.report.predefined_interval', 'NOT_FOUND')}
                                    title={localizationService.toLanguageString('configuration.analysis.report.predefined_interval', 'NOT_FOUND')}
                                />
                            </div>
                        </div>

                        <div className="report-types-box">
                            <div className="report-types-grid">
                                {checkboxLabels.map((label, i) => (
                                    <div key={label.id}>
                                        <Checkbox
                                            label={label.name}
                                            checked={selectedTypes.includes(label.id)}
                                            onChange={() => toggleType(label.id)}
                                        />

                                        {/* Figli solo per l'ultima checkbox */}
                                        {i === checkboxLabels.length - 1 && showAuxFields && (
                                            <div className="report-aux-fields">
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                                                        {localizationService.toLanguageString('configuration.analysis.report.min_pause_interval', 'NOT_FOUND')}
                                                    </label>
                                                    <Input
                                                        placeholder={localizationService.toLanguageString('configuration.analysis.report.enter_value', 'NOT_FOUND')}
                                                        style={{ width: '130px', margin: '0px 0px 0px 10px' }}
                                                    />
                                                </div>

                                                <div>
                                                    <Checkbox
                                                        label={localizationService.toLanguageString('configuration.analysis.report.exclude_zero_access', 'NOT_FOUND')}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
                                                        {localizationService.toLanguageString('configuration.analysis.report.acceleration_threshold', 'NOT_FOUND')}
                                                    </label>
                                                    <Input
                                                        placeholder={localizationService.toLanguageString('configuration.analysis.report.enter_value', 'NOT_FOUND')}
                                                        style={{ width: '130px', margin: '0px 0px 0px 10px' }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {
                        //advanced timeslot
                        isAdvanced &&
                        <div className="report-advanced-block">
                            <label className="report-section-title">{localizationService.toLanguageString('configuration.analysis.report.pick_interval', 'NOT_FOUND')}</label>
                            <div className="report-date-time-row">
                                <label className="report-date-time-label">{localizationService.toLanguageString('configuration.analysis.report.from', 'NOT_FOUND')}</label>
                                <DatePicker
                                    name="fromDate"
                                    value={dateTimeRange.fromDate}
                                    format="dd/MM/yyyy"
                                    width={190}
                                    onChange={handleChangeDate}
                                    onBlur={handleBlurDate}
                                />
                                <div className="report-time-control">
                                    <TimePicker
                                        name="fromTime"
                                        value={dateTimeRange.fromTime}
                                        format={isEnglishLanguage ? "hh:mm:ss a" : "HH:mm:ss"}
                                        width={150}
                                        onChange={handleChangeTime}
                                        onBlur={handleBlurTime}
                                    />
                                </div>
                            </div>
                            <div className="report-date-time-row">
                                <label className="report-date-time-label">{localizationService.toLanguageString('configuration.analysis.report.to', 'NOT_FOUND')}</label>
                                <DatePicker
                                    name="toDate"
                                    value={dateTimeRange.toDate}
                                    format="dd/MM/yyyy"
                                    width={190}
                                    min={dateTimeRange.fromDate || undefined}
                                    onChange={handleChangeDate}
                                    onBlur={handleBlurDate}
                                />
                                <div className="report-time-control">
                                    <TimePicker
                                        name="toTime"
                                        value={dateTimeRange.toTime}
                                        format={isEnglishLanguage ? "hh:mm:ss a" : "HH:mm:ss"}
                                        width={150}
                                        min={dateTimeRange.fromTime || undefined}
                                        onChange={handleChangeTime}
                                        onBlur={handleBlurTime}
                                    />
                                </div>
                            </div>
                        </div>
                    }

                    {/*scheduled report*/}
                    {typeReportAdvanced && (
                        <div className="report-scheduled-controls" >

                            <div 
                                className="report-panel report-days-box report-days-shell" 
                                style={{ ...panelStyle, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '18px' }}
                            >

                                <h3 className="report-days-title" >{localizationService.toLanguageString('configuration.analysis.report.days', 'NOT_FOUND')}</h3>

                                {/*days*/}
                                <div className="report-days-grid">
                                    {days.map((day: any) => (
                                        <div key={day.value} className="report-day-item">
                                            <div style={{ marginBottom: '4px' }}>
                                                {day.name}
                                            </div>
                                            <Checkbox
                                                checked={selectedDays.includes(day.value)}
                                                onChange={() => toggleDay(day.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/*timepicker*/}
                            <div className="report-panel report-slider-box" style={{ ...panelStyle, height: "140px" }}>
                                <div style={{ display: 'flex', gap: '18px', flexDirection: 'column' }}>
                                    <h3>{localizationService.toLanguageString('configuration.analysis.report.hour', 'NOT_FOUND')}</h3>

                                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                                        <TimePicker
                                            value={toPickerTime(hour)}
                                            format={isEnglishLanguage ? "hh:mm a" : "HH:mm"}
                                            width={150}
                                            steps={{ minute: 60, second: 60 }}
                                            onChange={handleHourChange}
                                        />
                                        {/*<div className="report-hour-badge">
                                            {formatHour(hour)}
                                        </div>*/}
                                    </div>

                                </div>

                            </div>

                        </div>
                    )}

                    <div>
                        {!typeReportAdvanced ?
                            <>
                                {/*Bottoni Export */}
                                <div className="report-actions">
                                    <Button
                                        themeColor="primary"
                                        title={localizationService.toLanguageString('configuration.analysis.report.export_to_csv', 'NOT_FOUND')}
                                        onClick={() => handleExport("csv")}
                                        disabled={isExportDisabled}
                                    >
                                        {localizationService.toLanguageString('configuration.analysis.report.export_to_csv', 'NOT_FOUND')}
                                    </Button>
                                    <Button
                                        themeColor="primary"
                                        title={localizationService.toLanguageString('configuration.analysis.report.export_to_xlsx', 'NOT_FOUND')}
                                        onClick={() => handleExport("xlsx")}
                                        disabled={isExportDisabled}
                                    >
                                        {localizationService.toLanguageString('configuration.analysis.report.export_to_xlsx', 'NOT_FOUND')}
                                    </Button>
                                </div>

                            </>
                            :
                            <div className="report-actions">
                                <Button
                                    themeColor="primary"
                                    title={localizationService.toLanguageString('configuration.analysis.report.execute', 'NOT_FOUND')}
                                    onClick={execute}
                                    disabled={isExecuteDisabled}
                                >
                                    {localizationService.toLanguageString('configuration.analysis.report.execute', 'NOT_FOUND')}
                                </Button>
                            </div>
                        }

                    </div>

                    { (scheduledReports.length > 0 ? (
                        <Card className="report-panel report-scheduled-box report-scheduled-card" style={scheduledCardStyle}>
                            <CardHeader className="k-hbox">
                                <div>
                                    <CardTitle style={settingTitleStyle}>
                                        {localizationService.toLanguageString('configuration.analysis.report.scheduled', 'NOT_FOUND')}
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="report-scheduled-table-wrapper">
                                    <table className="report-scheduled-table">
                                        <thead>
                                            <tr>
                                                <th>{localizationService.toLanguageString('configuration.analysis.report.interval', 'NOT_FOUND')}</th>
                                                <th>{localizationService.toLanguageString('configuration.analysis.report.type_description', 'NOT_FOUND')}</th>
                                                <th>{localizationService.toLanguageString('configuration.analysis.report.days', 'NOT_FOUND')}</th>
                                                <th>{localizationService.toLanguageString('configuration.analysis.report.hour', 'NOT_FOUND')}</th>
                                                <th>{localizationService.toLanguageString('configuration.analysis.report.delete', 'NOT_FOUND')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scheduledReports.map((report, idx) => (
                                                <tr key={report.id ?? idx}>
                                                    <td>{options.find((option: any) => option.id === report.typeId)?.name}</td>
                                                    <td>{report.reportTypes?.map(typeId => checkboxLabels.find((type: any) => type.id === typeId)?.name).join(', ')}</td>
                                                    <td>{formatScheduledDays(report.days)}</td>
                                                    <td>{formatScheduledUtcHourToLocal(report.hour)}</td>
                                                    <td>
                                                        <Button
                                                            className="report-delete-btn"
                                                            themeColor="error"
                                                            onClick={() => {
                                                                if (report.id) {
                                                                    setConfirmMessage({
                                                                        reportId: report.id,
                                                                        message: localizationService.toLanguageString('configuration.analysis.report.delete_confirm', 'Sei sicuro di voler eliminare questo report schedulato?')
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {localizationService.toLanguageString('configuration.analysis.report.delete', 'NOT_FOUND')}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    ) : (
                        <Card className="report-panel report-scheduled-box report-scheduled-card" style={scheduledCardStyle}>
                            <CardHeader className="k-hbox">
                                <div>
                                    <CardTitle style={settingTitleStyle}>
                                        {localizationService.toLanguageString('configuration.analysis.report.scheduled', 'NOT_FOUND')}
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <p style={{ margin: 0, color: '#1c425f', fontWeight: 600 }}>
                                    {localizationService.toLanguageString('configuration.analysis.report.no_scheduled_reports', 'No scheduled reports')}
                                </p>
                            </CardBody>
                        </Card>
                    ))}

                </div>

            </div>
        </>
    );
};